
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {EmailTemplateModifyResponse} from './EmailTemplateModifyResponse';
import {EventType} from '../core/EventType';
import {FieldModelEventType} from '../core/EventType';

/**
 * EmailTemplateModifyRequest struct
 */
class EmailTemplateModifyRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} emailTemplateId
   * @param {string=} emailTemplateName
   * @param {string=} newName
   * @param {string=} newComment
   * @param {string=} newDescription
   * @param {EventType=} newEventType
   * @param {string=} newEmailSubject
   * @param {string=} newEmailBody
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), emailTemplateId = null, emailTemplateName = null, newName = null, newComment = null, newDescription = null, newEventType = null, newEmailSubject = null, newEmailBody = null) {
    super()
    super.copy(parent)
    this.EmailTemplateId = emailTemplateId
    this.EmailTemplateName = emailTemplateName
    this.NewName = newName
    this.NewComment = newComment
    this.NewDescription = newDescription
    this.NewEventType = newEventType
    this.NewEmailSubject = newEmailSubject
    this.NewEmailBody = newEmailBody
  }

  /**
   * Copy struct (shallow copy)
   * @this {!EmailTemplateModifyRequest}
   * @param {!EmailTemplateModifyRequest} other Other struct
   * @returns {!EmailTemplateModifyRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.EmailTemplateId != null) {
      this.EmailTemplateId = new UUID(other.EmailTemplateId)
    } else {
      this.EmailTemplateId = null
    }
    if (other.EmailTemplateName != null) {
      this.EmailTemplateName = other.EmailTemplateName
    } else {
      this.EmailTemplateName = null
    }
    if (other.NewName != null) {
      this.NewName = other.NewName
    } else {
      this.NewName = null
    }
    if (other.NewComment != null) {
      this.NewComment = other.NewComment
    } else {
      this.NewComment = null
    }
    if (other.NewDescription != null) {
      this.NewDescription = other.NewDescription
    } else {
      this.NewDescription = null
    }
    if (other.NewEventType != null) {
      this.NewEventType = EventType.fromObject(other.NewEventType)
    } else {
      this.NewEventType = null
    }
    if (other.NewEmailSubject != null) {
      this.NewEmailSubject = other.NewEmailSubject
    } else {
      this.NewEmailSubject = null
    }
    if (other.NewEmailBody != null) {
      this.NewEmailBody = other.NewEmailBody
    } else {
      this.NewEmailBody = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!EmailTemplateModifyRequest}
   * @returns {!EmailTemplateModifyRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new EmailTemplateModifyRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new EmailTemplateModifyRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!EmailTemplateModifyRequest}
   * @param {!EmailTemplateModifyRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof EmailTemplateModifyRequest)) {
      throw new TypeError('Instance of EmailTemplateModifyRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!EmailTemplateModifyRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      EmailTemplateId: ((this.EmailTemplateId != null) ? this.EmailTemplateId.toString() : null),
      EmailTemplateName: ((this.EmailTemplateName != null) ? this.EmailTemplateName : null),
      NewName: ((this.NewName != null) ? this.NewName : null),
      NewComment: ((this.NewComment != null) ? this.NewComment : null),
      NewDescription: ((this.NewDescription != null) ? this.NewDescription : null),
      NewEventType: ((this.NewEventType != null) ? this.NewEventType : null),
      NewEmailSubject: ((this.NewEmailSubject != null) ? this.NewEmailSubject : null),
      NewEmailBody: ((this.NewEmailBody != null) ? this.NewEmailBody : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return EmailTemplateModifyRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!EmailTemplateModifyRequest} other Object value
   * @returns {!EmailTemplateModifyRequest} Created struct
   */
  static fromObject (other) {
    return new EmailTemplateModifyRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!EmailTemplateModifyRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return EmailTemplateModifyRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!EmailTemplateModifyRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6417
  }
}

export { EmailTemplateModifyRequest };

/**
 * Fast Binary Encoding EmailTemplateModifyRequest field model
 */
class FieldModelEmailTemplateModifyRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.EmailTemplateId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.EmailTemplateName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.EmailTemplateId.fbeOffset + this.EmailTemplateId.fbeSize), buffer, this.EmailTemplateId.fbeOffset + this.EmailTemplateId.fbeSize)
    this.NewName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.EmailTemplateName.fbeOffset + this.EmailTemplateName.fbeSize), buffer, this.EmailTemplateName.fbeOffset + this.EmailTemplateName.fbeSize)
    this.NewComment = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewName.fbeOffset + this.NewName.fbeSize), buffer, this.NewName.fbeOffset + this.NewName.fbeSize)
    this.NewDescription = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewComment.fbeOffset + this.NewComment.fbeSize), buffer, this.NewComment.fbeOffset + this.NewComment.fbeSize)
    this.NewEventType = new fbe.FieldModelOptional(new FieldModelEventType(buffer, this.NewDescription.fbeOffset + this.NewDescription.fbeSize), buffer, this.NewDescription.fbeOffset + this.NewDescription.fbeSize)
    this.NewEmailSubject = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewEventType.fbeOffset + this.NewEventType.fbeSize), buffer, this.NewEventType.fbeOffset + this.NewEventType.fbeSize)
    this.NewEmailBody = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewEmailSubject.fbeOffset + this.NewEmailSubject.fbeSize), buffer, this.NewEmailSubject.fbeOffset + this.NewEmailSubject.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.EmailTemplateId.fbeSize + this.EmailTemplateName.fbeSize + this.NewName.fbeSize + this.NewComment.fbeSize + this.NewDescription.fbeSize + this.NewEventType.fbeSize + this.NewEmailSubject.fbeSize + this.NewEmailBody.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.EmailTemplateId.fbeExtra + this.EmailTemplateName.fbeExtra + this.NewName.fbeExtra + this.NewComment.fbeExtra + this.NewDescription.fbeExtra + this.NewEventType.fbeExtra + this.NewEmailSubject.fbeExtra + this.NewEmailBody.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelEmailTemplateModifyRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6417
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.EmailTemplateId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.EmailTemplateId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailTemplateId.fbeSize

    if ((fbeCurrentSize + this.EmailTemplateName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.EmailTemplateName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailTemplateName.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.NewComment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewComment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewComment.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewDescription.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize

    if ((fbeCurrentSize + this.NewEventType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewEventType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewEventType.fbeSize

    if ((fbeCurrentSize + this.NewEmailSubject.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewEmailSubject.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewEmailSubject.fbeSize

    if ((fbeCurrentSize + this.NewEmailBody.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewEmailBody.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewEmailBody.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @param {!EmailTemplateModifyRequest} fbeValue Default value, defaults is new EmailTemplateModifyRequest()
   * @returns {!EmailTemplateModifyRequest} EmailTemplateModifyRequest value
   */
  get (fbeValue = new EmailTemplateModifyRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @param {!EmailTemplateModifyRequest} fbeValue EmailTemplateModifyRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.EmailTemplateId.fbeSize) <= fbeStructSize) {
      fbeValue.EmailTemplateId = this.EmailTemplateId.get()
    } else {
      fbeValue.EmailTemplateId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailTemplateId.fbeSize

    if ((fbeCurrentSize + this.EmailTemplateName.fbeSize) <= fbeStructSize) {
      fbeValue.EmailTemplateName = this.EmailTemplateName.get()
    } else {
      fbeValue.EmailTemplateName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailTemplateName.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) <= fbeStructSize) {
      fbeValue.NewName = this.NewName.get()
    } else {
      fbeValue.NewName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.NewComment.fbeSize) <= fbeStructSize) {
      fbeValue.NewComment = this.NewComment.get()
    } else {
      fbeValue.NewComment = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewComment.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) <= fbeStructSize) {
      fbeValue.NewDescription = this.NewDescription.get()
    } else {
      fbeValue.NewDescription = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize

    if ((fbeCurrentSize + this.NewEventType.fbeSize) <= fbeStructSize) {
      fbeValue.NewEventType = this.NewEventType.get()
    } else {
      fbeValue.NewEventType = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewEventType.fbeSize

    if ((fbeCurrentSize + this.NewEmailSubject.fbeSize) <= fbeStructSize) {
      fbeValue.NewEmailSubject = this.NewEmailSubject.get()
    } else {
      fbeValue.NewEmailSubject = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewEmailSubject.fbeSize

    if ((fbeCurrentSize + this.NewEmailBody.fbeSize) <= fbeStructSize) {
      fbeValue.NewEmailBody = this.NewEmailBody.get()
    } else {
      fbeValue.NewEmailBody = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewEmailBody.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @param {!EmailTemplateModifyRequest} fbeValue EmailTemplateModifyRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelEmailTemplateModifyRequest}
   * @param {EmailTemplateModifyRequest} fbeValue EmailTemplateModifyRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.EmailTemplateId.set(fbeValue.EmailTemplateId)
    this.EmailTemplateName.set(fbeValue.EmailTemplateName)
    this.NewName.set(fbeValue.NewName)
    this.NewComment.set(fbeValue.NewComment)
    this.NewDescription.set(fbeValue.NewDescription)
    this.NewEventType.set(fbeValue.NewEventType)
    this.NewEmailSubject.set(fbeValue.NewEmailSubject)
    this.NewEmailBody.set(fbeValue.NewEmailBody)
  }
}

export { FieldModelEmailTemplateModifyRequest };

/**
 * Fast Binary Encoding EmailTemplateModifyRequest model
 */
class EmailTemplateModifyRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelEmailTemplateModifyRequest(this.buffer, 4)
  }

  /**
   * Get the EmailTemplateModifyRequest model
   * @this {!EmailTemplateModifyRequestModel}
   * @returns {!FieldModelEmailTemplateModifyRequest} model EmailTemplateModifyRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!EmailTemplateModifyRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!EmailTemplateModifyRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return EmailTemplateModifyRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!EmailTemplateModifyRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelEmailTemplateModifyRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!EmailTemplateModifyRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!EmailTemplateModifyRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!EmailTemplateModifyRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!EmailTemplateModifyRequestModel}
   * @param {!EmailTemplateModifyRequest} value EmailTemplateModifyRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!EmailTemplateModifyRequestModel}
   * @param {!EmailTemplateModifyRequest} value EmailTemplateModifyRequest value, defaults is new EmailTemplateModifyRequest()
   * @return {!object} Deserialized EmailTemplateModifyRequest value and its size
   */
  deserialize (value = new EmailTemplateModifyRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new EmailTemplateModifyRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new EmailTemplateModifyRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!EmailTemplateModifyRequestModel}
   * @param {!number} prev Previous EmailTemplateModifyRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { EmailTemplateModifyRequestModel };
EmailTemplateModifyRequest.__has_response = true;
EmailTemplateModifyRequest.__response_class = EmailTemplateModifyResponse;
